/**
 * "Export as Word" for a single answer: the whole pipeline, in order.
 *
 *   parts → segments (text vs chart)     — messageExportSegments
 *         → document blocks              — markdownToDocBlocks / chartImage   (main thread)
 *         → .docx bytes                  — docx.worker                        (off thread)
 *         → a download                   — downloadBlob
 *
 * Everything happens in the browser; no server is involved. Only the packing goes to the worker —
 * the OOXML and the zip deflate around megabytes of chart PNG, which is what actually froze the
 * thread. The two steps before it stay here: charts because Highcharts needs a real element, and
 * markdown because micromark's entity decoder touches `document` at import time (see docx.worker).
 *
 * It also owns the user-facing failure report, because it is invoked from the thread's message menu
 * (see MessageAction) which closes on click and reports nothing itself.
 */
import { platformToast } from "$root/notification-system/toast";
import { downloadBlob } from "$root/utils/files";
import { wrap } from "comlink";
import { renderChartImage } from "./chartImage";
import { resolveToolLink } from "../toolLinks";
import type { DocBlock } from "./docModel";
import DocxWorker from "./docx.worker?worker";
import type { DocxWorkerApi } from "./docx.worker";
import { markdownToDocBlocks } from "./markdownToDocBlocks";
import { collectMessageExportSegments, type ExportablePart } from "./messageExportSegments";

export type ExportMessageAsDocxOptions = {
	/** Name the file is saved under, extension included. */
	readonly fileName: string;
	/** First line of the document, and its Word title property. */
	readonly title?: string;
	/** Quieter line under the title — in practice when the answer was written. */
	readonly subtitle?: string;
};

export async function exportMessageAsDocx(
	parts: readonly ExportablePart[],
	options: ExportMessageAsDocxOptions,
): Promise<void> {
	// Started before the charts are drawn so the worker boots while the main thread is still busy.
	// One worker per export, terminated below: an export is a rare, one-shot job, and a resident
	// worker would hold the `docx` module in memory for a menu item most turns never use.
	const worker = new DocxWorker();

	try {
		const blocks: DocBlock[] = [];
		// Ordered lists number by instance across the WHOLE document, so the counter is threaded
		// through the segments rather than restarting inside each one.
		let nextListInstance = 1;

		for (const segment of collectMessageExportSegments(parts)) {
			if (segment.kind === "markdown") {
				const converted = markdownToDocBlocks(segment.markdown, {
					firstListInstance: nextListInstance,
					resolveRef: resolveToolLink,
				});
				blocks.push(...converted.blocks);
				nextListInstance = converted.nextListInstance;
				continue;
			}
			// Sequential with a yield between charts, not parallel: each one briefly mounts a real
			// Highcharts instance and rasterizes it, which the main thread has to do — the yield is what
			// lets the browser paint in between instead of freezing for the length of the whole batch.
			// A chart that can't be rasterized fails the export rather than quietly going missing: a
			// document the reader can't tell is incomplete is worse than no document.
			await yieldToBrowser();
			blocks.push(await renderChartImage(segment.spec));
		}

		const blob = await wrap<DocxWorkerApi>(worker).build(blocks, {
			title: options.title,
			subtitle: options.subtitle,
		});
		downloadBlob(blob, { fileName: options.fileName });
	} catch (error) {
		console.error("Sphere AI: Word export failed", error);
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children: "Couldn't export this answer as a Word document. Please try again.",
		});
	} finally {
		worker.terminate();
	}
}

/** A macrotask, not a frame: `requestAnimationFrame` never fires in a backgrounded tab. */
function yieldToBrowser(): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, 0));
}
